import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Printer, ArrowLeft, Upload, Loader2, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminGallery() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setItems(data);
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.email) load();
  }, [user, load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      try {
        await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    setCaption("");
    e.target.value = "";
    toast.success("Photos added to gallery");
    load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed from gallery");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!user || !user.email) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><Printer size={20} /></span>
            <h1 className="font-heading text-base font-extrabold text-slate-900">Gallery Manager</h1>
          </div>
          <Link to="/admin/dashboard" data-testid="back-to-dashboard">
            <Button variant="outline" size="sm" className="gap-2"><ArrowLeft size={15} /> Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-lg font-bold text-slate-900">Add work photos</h2>
          <p className="mt-1 text-sm text-slate-500">Uploaded photos replace the stock gallery on your website instantly. Any image format.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="gallery-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (optional) — e.g. LED board, Vidyaranyapura"
              className="sm:max-w-sm"
            />
            <label
              data-testid="gallery-upload-label"
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-amber-700 transition-colors"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Uploading…" : "Upload photos"}
              <input data-testid="gallery-file-input" type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
            </label>
          </div>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div data-testid="gallery-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-400">
              <ImageIcon size={40} className="mx-auto" />
              <p className="mt-3 font-medium">No photos yet — your website shows stock images until you upload.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {items.map((it) => (
                <div key={it.id} data-testid={`gallery-manage-${it.id}`} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <img src={`${API_BASE}/gallery/${it.id}/image`} alt={it.caption || "gallery"} className="h-40 w-full object-cover" />
                  <div className="p-2">
                    <p className="truncate text-xs text-slate-600">{it.caption || it.original_filename}</p>
                  </div>
                  <button
                    data-testid={`gallery-delete-${it.id}`}
                    onClick={() => remove(it.id)}
                    className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-red-500 shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
